import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight } from "lucide-react";
import PhoneFrame from "./PhoneFrame";
import { APP_SCREENSHOTS } from "../../lib/assets";

export const AppPreview = () => {
  const { t } = useTranslation();
  const trackRef = useRef(null);
  const [active, setActive] = useState(0);

  const scrollTo = (idx) => {
    const track = trackRef.current;
    if (!track) return;
    const card = track.children[idx];
    if (card) {
      track.scrollTo({
        left: card.offsetLeft - 24,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const onScroll = () => {
      const children = Array.from(track.children);
      let closest = 0;
      let min = Infinity;
      children.forEach((c, i) => {
        const d = Math.abs(c.offsetLeft - track.scrollLeft - 24);
        if (d < min) {
          min = d;
          closest = i;
        }
      });
      setActive(closest);
    };
    track.addEventListener("scroll", onScroll, { passive: true });
    return () => track.removeEventListener("scroll", onScroll);
  }, []);

  const next = () => scrollTo(Math.min(active + 1, APP_SCREENSHOTS.length - 1));
  const prev = () => scrollTo(Math.max(active - 1, 0));

  return (
    <section
      id="app-preview"
      data-testid="app-preview-section"
      className="bg-background py-24 lg:py-32"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--sand-dark))]">
              {t("appPreview.eyebrow")}
            </p>
            <h2 className="mt-4 font-display text-4xl font-medium leading-[1.05] text-[hsl(var(--ink))] text-balance sm:text-5xl lg:text-6xl">
              {t("appPreview.title")}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="preview-prev"
              onClick={prev}
              aria-label={t("appPreview.prev")}
              className="grid h-11 w-11 place-items-center rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--surface))] text-[hsl(var(--ink))] transition-all hover:bg-[hsl(var(--ink))] hover:text-[hsl(var(--background))]"
            >
              <ChevronLeft className="h-5 w-5" strokeWidth={1.5} />
            </button>
            <button
              type="button"
              data-testid="preview-next"
              onClick={next}
              aria-label={t("appPreview.next")}
              className="grid h-11 w-11 place-items-center rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--surface))] text-[hsl(var(--ink))] transition-all hover:bg-[hsl(var(--ink))] hover:text-[hsl(var(--background))]"
            >
              <ChevronRight className="h-5 w-5" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </div>

      <div
        ref={trackRef}
        className="hide-scrollbar mt-14 flex snap-x snap-mandatory gap-6 overflow-x-auto px-6 pb-8 lg:px-10"
      >
        {APP_SCREENSHOTS.map((s, i) => (
          <article
            key={s.name}
            data-testid={`preview-slide-${i}`}
            className="snap-start shrink-0 first:ml-0"
            style={{ width: "min(82vw, 360px)" }}
          >
            <div className="rounded-[2rem] border border-[hsl(var(--border))] bg-[hsl(var(--surface-secondary))] p-6">
              <PhoneFrame src={s.url} alt={s.name} className="!max-w-[260px]" />
              <div className="mt-6">
                <p className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--ink-soft))]">
                  {s.label}
                </p>
                <p className="mt-2 font-display text-xl text-[hsl(var(--ink))]">
                  {s.caption}
                </p>
              </div>
            </div>
          </article>
        ))}
      </div>

      <div className="mx-auto flex max-w-7xl items-center justify-center gap-2 px-6 lg:px-10">
        {APP_SCREENSHOTS.map((_, i) => (
          <button
            key={i}
            type="button"
            aria-label={`${t("appPreview.gotoSlide")} ${i + 1}`}
            onClick={() => scrollTo(i)}
            className={`h-1.5 rounded-full transition-all ${
              active === i
                ? "w-8 bg-[hsl(var(--forest))]"
                : "w-3 bg-[hsl(var(--border))] hover:bg-[hsl(var(--ink-soft))]"
            }`}
          />
        ))}
      </div>
    </section>
  );
};

export default AppPreview;
